import { useCallback, useEffect, useState } from 'react';
import { C } from '../lib/theme';
import { styles } from '../lib/styles';
import { fmtDateTime } from '../lib/util';
import { getCurrentUser } from '../auth/useUser';
import { SearchFilterBar, useSearchFilter } from '../components/SearchFilterBar';
import { useAppData } from '../state/contexts/AppDataContext';
import { useUIState } from '../state/contexts/UIStateContext';
import { ApiProblem } from '../api/client';
import {
  listUsers,
  changeUserRole,
  deleteUser,
  setUserEnabled,
  sendPasswordResetEmail,
  resetUserTotp,
  type AdminUser,
  type BcidRole,
} from '../api/users';

// #139 — in-portal user management. bcid-admin only; the API 503s in stub mode
// (until bcid-admin-svc is provisioned), which renders a not-configured banner.
// Guardrails (self-change, last-admin, AAD-managed) are enforced server-side;
// the UI mirrors them for affordance and surfaces the 409 detail via a toast.

const ROLE_LABELS: Record<BcidRole, string> = {
  'bcid-admin': 'Admin',
  'bcid-analyst': 'Analyst',
  'bcid-approver': 'Approver',
  'bcid-legal': 'Legal',
};
const ROLE_OPTIONS = Object.keys(ROLE_LABELS) as BcidRole[];

export function UserManagement() {
  const user = getCurrentUser();
  const { showToast } = useAppData();
  const { modal, setModal, modalData } = useUIState();

  const [users, setUsers] = useState<AdminUser[] | null>(null);
  const [notConfigured, setNotConfigured] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  // #463 — which row's delete is awaiting confirmation. One at a time, and
  // never pre-armed: deleting is the only irreversible action on this page.
  const [confirmDelete, setConfirmDelete] = useState<string | null>(null);

  const load = useCallback(() => {
    setLoadError(null);
    return listUsers()
      .then((items) => { setUsers(items); setNotConfigured(false); })
      .catch((err: unknown) => {
        if (err instanceof ApiProblem && err.status === 503) { setNotConfigured(true); setUsers([]); return; }
        setLoadError(err instanceof Error ? err.message : String(err));
      });
  }, []);

  useEffect(() => { load(); }, [load]);

  // Re-load when the create modal closes after a successful create (it stashes
  // modalData.created), so the new user appears without a manual refresh.
  useEffect(() => {
    if (modal === null && modalData?.usersCreated) load();
  }, [modal, modalData?.usersCreated, load]);

  const reportError = useCallback((err: unknown) => {
    if (err instanceof ApiProblem) showToast(err.detail ? `${err.title}: ${err.detail}` : err.title);
    else showToast(err instanceof Error ? err.message : String(err));
  }, [showToast]);

  // Runs a mutation, then reloads the list. Guards double-clicks with busyId.
  const run = useCallback(async (id: string, fn: () => Promise<void>, successMsg?: string) => {
    setBusyId(id);
    try {
      await fn();
      await load();
      if (successMsg) showToast(successMsg, 'success');
    } catch (err) {
      reportError(err);
    } finally {
      setBusyId(null);
    }
  }, [load, reportError, showToast]);

  // Client-side v1 search + role/sign-in/status facets over the loaded users.
  // Called unconditionally (before the admin early-return) to keep hook order
  // stable. IdP options are derived from whatever providers appear in the list.
  const userList = users ?? [];
  const idpOptions = Array.from(
    new Set(userList.flatMap((u) => (u.local ? ['Local'] : u.identityProviders))),
  ).sort();
  const { filtered: filteredUsers, bar } = useSearchFilter(
    userList,
    (u) => [u.name, u.email, u.username, u.role].filter(Boolean).join(' '),
    [
      {
        id: 'role',
        label: 'Role',
        options: [
          { value: '', label: 'All roles' },
          ...ROLE_OPTIONS.map((r) => ({ value: r, label: ROLE_LABELS[r] })),
        ],
        predicate: (u, v) => u.role === v,
      },
      {
        id: 'signin',
        label: 'Sign-in',
        options: [
          { value: '', label: 'All sign-in methods' },
          ...idpOptions.map((p) => ({ value: p, label: p })),
        ],
        predicate: (u, v) => (v === 'Local' ? u.local : u.identityProviders.includes(v)),
      },
      {
        id: 'enabled',
        label: 'Status',
        options: [
          { value: '', label: 'All statuses' },
          { value: 'enabled', label: 'Enabled only' },
          { value: 'disabled', label: 'Disabled only' },
        ],
        predicate: (u, v) => (v === 'enabled' ? u.enabled : !u.enabled),
      },
    ],
    { placeholder: 'Search users by name, email, or username…' },
  );

  // Defense-in-depth: setPage is client state, so a non-admin can't be trusted
  // to have been kept off this page. The API 403s regardless.
  if (!user?.canAdmin) {
    return (
      <div>
        <h2 style={{ fontSize: 20, fontWeight: 700, color: C.text, marginBottom: 20 }}>User Management</h2>
        <div style={{ ...styles.card, color: C.danger }}>Administrator access required.</div>
      </div>
    );
  }

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
        <h2 style={{ fontSize: 20, fontWeight: 700, color: C.text }}>User Management</h2>
        {!notConfigured && (
          <button style={{ ...styles.btn, ...styles.btnGold }} onClick={() => setModal('create-user')}>+ Create User</button>
        )}
      </div>
      <div style={{ color: C.textMuted, fontSize: 13, marginBottom: 20 }}>
        Manage portal accounts, roles, and credential resets. Actions are audited.
      </div>

      {notConfigured && (
        <div style={{ ...styles.card, borderLeft: `3px solid ${C.warn}`, color: C.warn }} role="alert">
          <strong>User management is not configured.</strong> The Keycloak admin service account
          (<code>bcid-admin-svc</code>) has not been provisioned on this deployment yet. Until then,
          manage users in the Keycloak admin console. See the user-management runbook.
        </div>
      )}

      {loadError && (
        <div style={{ ...styles.card, color: C.danger }} role="alert">Failed to load users: {loadError}</div>
      )}

      {!notConfigured && !loadError && users && (
        <div style={styles.card}>
          <SearchFilterBar {...bar} />
          <table style={styles.table}>
            <thead><tr>
              <th style={styles.th}>Name / Email</th>
              <th style={styles.th}>Role</th>
              <th style={styles.th}>Status</th>
              <th style={styles.th}>Sign-in</th>
              <th style={styles.th}>Last login</th>
              <th style={styles.th}>Actions</th>
            </tr></thead>
            <tbody>
              {filteredUsers.map((u) => {
                const isSelf = u.keycloakId === user.id;
                const locked = isSelf || u.aadManaged;
                const busy = busyId === u.keycloakId;
                return (
                  <tr key={u.keycloakId}>
                    <td style={styles.td}>
                      <div style={{ fontWeight: 600 }}>{u.name}{isSelf && <span style={{ color: C.textMuted, fontWeight: 400 }}> (you)</span>}</div>
                      <div style={{ fontSize: 12, color: C.textMuted }}>{u.email ?? u.username}</div>
                    </td>
                    <td style={styles.td}>
                      <select
                        aria-label={`Role for ${u.name}`}
                        style={{ ...styles.select, padding: '6px 10px' }}
                        value={u.role ?? ''}
                        disabled={locked || busy}
                        onChange={(e) => run(u.keycloakId, () => changeUserRole(u.keycloakId, e.target.value as BcidRole))}
                      >
                        {u.role === null && <option value="" disabled>— none —</option>}
                        {ROLE_OPTIONS.map((r) => <option key={r} value={r}>{ROLE_LABELS[r]}</option>)}
                      </select>
                    </td>
                    <td style={styles.td}>
                      <span style={{ ...styles.badge, background: u.enabled ? C.successBg : C.dangerBg, color: u.enabled ? C.success : C.danger }}>
                        {u.enabled ? 'Enabled' : 'Disabled'}
                      </span>
                    </td>
                    <td style={styles.td}>
                      {u.local
                        ? <span style={{ fontSize: 12, color: C.textMuted }}>Local</span>
                        : <span style={{ fontSize: 12, color: C.textMuted }}>{u.identityProviders.join(', ') || 'Federated'}</span>}
                      {u.aadManaged && (
                        <div>
                          <span style={{ ...styles.badge, background: C.goldBg, color: C.goldText, marginTop: 4 }}>
                            AAD-group managed
                          </span>
                          {/* #251: was a title= tooltip — hover-only, so it never
                              reached keyboard or touch users. Always visible now. */}
                          <div style={{ fontSize: 11, color: C.textMuted, marginTop: 2 }}>
                            Admin membership is managed by the SGU-BCID-Admins Entra ID group
                          </div>
                        </div>
                      )}
                    </td>
                    <td style={styles.td}>{u.lastLogin ? fmtDateTime(u.lastLogin) : '—'}</td>
                    <td style={styles.td}>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                        <button
                          style={{ ...styles.btn, ...styles.btnOutline, ...styles.btnSmall }}
                          disabled={locked || busy}
                          onClick={() => run(u.keycloakId, () => setUserEnabled(u.keycloakId, !u.enabled))}
                        >{u.enabled ? 'Disable' : 'Enable'}</button>
                        {/* #463 — Delete is offered only on a DISABLED account
                            and never on your own, because the API refuses both
                            and a button that always fails reads as a broken
                            page. Disabling stays the primary action; deleting is
                            the second, deliberate step. `locked` covers the
                            AAD-managed case, where membership is Entra's to
                            change. */}
                        {!u.enabled && !isSelf && !u.aadManaged && (
                          confirmDelete === u.keycloakId ? (
                            <>
                              <button
                                style={{ ...styles.btn, ...styles.btnDanger, ...styles.btnSmall }}
                                disabled={busy}
                                onClick={() => {
                                  setConfirmDelete(null);
                                  void run(
                                    u.keycloakId,
                                    () => deleteUser(u.keycloakId),
                                    `${u.name}'s account is deleted. Their case history and audit trail are unchanged.`,
                                  );
                                }}
                              >Confirm delete</button>
                              <button
                                style={{ ...styles.btn, ...styles.btnOutline, ...styles.btnSmall }}
                                disabled={busy}
                                onClick={() => setConfirmDelete(null)}
                              >Cancel</button>
                            </>
                          ) : (
                            <button
                              style={{ ...styles.btn, ...styles.btnOutline, ...styles.btnSmall }}
                              disabled={busy}
                              onClick={() => setConfirmDelete(u.keycloakId)}
                            >Delete</button>
                          )
                        )}
                        {u.local && (
                          <>
                            <button
                              style={{ ...styles.btn, ...styles.btnOutline, ...styles.btnSmall }}
                              disabled={busy}
                              onClick={() => run(u.keycloakId, () => sendPasswordResetEmail(u.keycloakId), 'Password-reset email sent.')}
                            >Send password reset</button>
                            <button
                              style={{ ...styles.btn, ...styles.btnOutline, ...styles.btnSmall }}
                              disabled={busy}
                              onClick={() => run(u.keycloakId, () => resetUserTotp(u.keycloakId), 'MFA reset — the user will re-enroll at next login.')}
                            >Reset MFA</button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
              {filteredUsers.length === 0 && (
                <tr><td style={styles.td} colSpan={6}><span style={{ color: C.textMuted }}>{users.length === 0 ? 'No users.' : 'No users match the current filters.'}</span></td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
